import React, { useEffect, useState } from "react";
import { getState, subscribe, categoryPercent, sectionMastery, overallProgress } from "@/lib/progress";
import { CATEGORIES } from "@/lib/content";
import { BADGES, STICKERS } from "@/lib/badges";
import { ProgressBar, StarBadge } from "@/components/Bits";

const GAME_ROWS = [{ id: "memory", emoji: "🧠", title: "Memory Game" }, { id: "games", emoji: "🎮", title: "Mini-Games" }];

export default function Progress() {
  const [s, setS] = useState(getState());
  useEffect(() => subscribe(setS), []);
  const overall = overallProgress();

  return (
    <div className="ll-bg min-h-screen pb-28 md:pb-8">
      <main className="max-w-4xl mx-auto px-4 sm:px-6 pt-6">
        <div className="text-center pt-4">
          <div className="text-purple-500 uppercase tracking-widest text-xs font-bold">Your Journey</div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl text-purple-900 mt-1">Look how much you've learned! 🌟</h1>
        </div>

        <div className="ll-card p-6 mt-8 bg-gradient-to-br from-violet-500 to-purple-600 text-white">
          <div className="flex items-center justify-between"><div className="font-display font-bold text-2xl">Overall progress</div><div className="font-display font-bold text-3xl" data-testid="stat-overall">{overall}%</div></div>
          <div className="mt-3"><ProgressBar percent={overall} testId="overall-progress" /></div>
          <div className="text-white/80 text-sm mt-2">Activities explored across all sections</div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
          <Stat emoji="⭐" v={s.stars} label="Stars Earned" bg="bg-yellow-50 border-yellow-200" id="stat-total-stars" />
          <Stat emoji="🔥" v={s.streak} label="Day Streak" bg="bg-orange-50 border-orange-200" id="stat-streak" />
          <Stat emoji="🎯" v={s.activitiesCompleted} label="Activities Done" bg="bg-emerald-50 border-emerald-200" id="stat-activities" />
          <Stat emoji="🏅" v={(s.badges || []).length} label="Badges" bg="bg-pink-50 border-pink-200" id="stat-badges" />
        </div>

        <div className="ll-card p-6 mt-6">
          <h2 className="font-display font-bold text-2xl text-purple-900">Learning Sections</h2>
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            {CATEGORIES.map(c => <Row key={c.id} id={c.id} emoji={c.emoji} title={c.short} pct={sectionMastery(c.id)} sub={`${categoryPercent(c.id)}% correct`} />)}
          </div>
          <h2 className="font-display font-bold text-2xl text-purple-900 mt-8">Games</h2>
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            {GAME_ROWS.map(g => <Row key={g.id} id={g.id} emoji={g.emoji} title={g.title} pct={categoryPercent(g.id)} sub={`${s.categories[g.id]?.correct || 0} wins`} />)}
          </div>
        </div>

        <div className="ll-card p-6 mt-6" data-testid="badges-section">
          <h2 className="font-display font-bold text-2xl text-purple-900">Achievement Badges</h2>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-4">
            {BADGES.map(b => {
              const has = (s.badges || []).includes(b.id);
              return (
                <div key={b.id} data-testid={`badge-${b.id}`} className={`rounded-2xl p-3 text-center border-2 ${has ? "bg-amber-50 border-amber-300" : "bg-purple-50/60 border-purple-100 opacity-60 grayscale"}`} title={b.desc}>
                  <div className="text-3xl">{b.emoji}</div>
                  <div className="font-bold text-xs text-purple-900 mt-1">{b.title}</div>
                  <div className="text-[10px] text-purple-500">{b.desc}</div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="ll-card p-6 mt-6" data-testid="stickers-section">
          <h2 className="font-display font-bold text-2xl text-purple-900">Sticker Rewards</h2>
          <p className="text-purple-600 text-sm font-semibold">Earn stars to unlock cute animal stickers!</p>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mt-4">
            {STICKERS.map(st => {
              const has = s.stars >= st.stars;
              return (
                <div key={st.id} data-testid={`sticker-${st.id}`} className={`rounded-2xl p-3 text-center border-2 ${has ? "bg-white border-emerald-200" : "bg-purple-50/60 border-purple-100"}`}>
                  <img src={st.img} alt={st.title} className={`w-16 h-16 mx-auto object-contain ${has ? "ll-float-slow" : "opacity-30 grayscale blur-[1px]"}`} />
                  <div className="font-bold text-xs text-purple-900 mt-1">{has ? st.title : `${st.stars} ⭐`}</div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="text-center mt-8">
          <StarBadge count={s.stars} testId="progress-stars" />
          <p className="mt-3 font-display text-xl text-purple-900">Keep exploring, super learner!</p>
        </div>
      </main>
    </div>
  );
}

function Stat({ emoji, v, label, bg, id }) {
  return (
    <div className={`ll-card p-5 ${bg} text-center`}>
      <div className="text-4xl">{emoji}</div>
      <div className="font-display text-3xl font-bold text-purple-900 mt-1" data-testid={id}>{v}</div>
      <div className="text-purple-700/80 font-bold text-sm">{label}</div>
    </div>
  );
}

function Row({ id, emoji, title, pct, sub }) {
  return (
    <div data-testid={`skill-row-${id}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2"><span className="text-2xl">{emoji}</span><span className="font-display font-bold text-purple-900">{title}</span></div>
        <span className="text-sm font-bold text-purple-600">{pct}% <span className="text-purple-400 font-semibold">· {sub}</span></span>
      </div>
      <ProgressBar percent={pct} />
    </div>
  );
}
